import { manifestApi } from "#/api/manifestApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import ExitStatusBadge from "#/components/manifests/ExitStatusBadge";
import ReleaseBadge from "#/components/manifests/ReleaseBadge";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import type { ColumnKey } from "#/config/tableColumnsConfig";
import { useClient } from "#/hook/useClient";
import { usePermission } from "#/hooks/usePermission";
import { useTableColumns, type ColumnRenderer } from "#/hooks/useTableColumns";
import type { HawbRow } from "#/types/manifest";
import { formatDate } from "#/utils/dateTimeUtils";
import { Button, Table, TextInput, Title } from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconEye, IconFileImport, IconPlus, IconSearch } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CreateHawbModal from "./components/CreateHawbModal";
import ImportManifestModal from "./components/ImportManifestModal";

/**
 * Shipment Manifests: one row per consignment (MAWB + HAWB). Consignments with
 * an exit issue come back first from the API and get a red outline, so they
 * never sink under the date sort.
 */
const ShipmentManifestsPage = () => {
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite } = usePermission();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [importOpened, { open: openImport, close: closeImport }] = useDisclosure(false);
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);

  const yesNo = (value: boolean) => (value ? t("common.yes") : t("common.no"));

  const columns: Record<ColumnKey<"shipmentManifests">, ColumnRenderer<HawbRow>> = {
    awb_number: {
      sortKey: "awb_number",
      cellClassName: "min-w-[150px] font-medium",
      cell: (row) => row.awb_number,
    },
    hawb_number: {
      sortKey: "hawb_number",
      cellClassName: "min-w-[100px]",
      cell: (row) => row.hawb_number || "—",
    },
    arrival_date: {
      sortKey: "arrival_date",
      cellClassName: "min-w-[120px]",
      cell: (row) => (row.arrival_date ? formatDate(row.arrival_date, client) : "--"),
    },
    origin_station: { cellClassName: "min-w-[80px]", cell: (row) => row.origin_station ?? "--" },
    packages: { sortKey: "packages", cellClassName: "min-w-[90px]", cell: (row) => row.packages },
    weight_kg: {
      cellClassName: "min-w-[80px]",
      cell: (row) => (row.weight_kg === null ? "--" : row.weight_kg),
    },
    storage_fee_paid: { cellClassName: "min-w-[110px]", cell: (row) => yesNo(row.storage_fee_paid) },
    blocked: { cellClassName: "min-w-[90px]", cell: (row) => yesNo(row.blocked) },
    has_pick_list: { cellClassName: "min-w-[90px]", cell: (row) => yesNo(row.has_pick_list) },
    release: {
      cellClassName: "min-w-[170px]",
      cell: (row) => <ReleaseBadge releaseBlockers={row.release_blockers} />,
    },
    tagged: {
      cellClassName: "min-w-[90px]",
      cell: (row) => `${row.tagged_count}/${row.packages}`,
    },
    exit_status: {
      sortKey: "exit_status",
      cellClassName: "min-w-[200px]",
      cell: (row) => <ExitStatusBadge status={row.exit_status} issues={row.exit_issues} />,
    },
  };
  const { visible, headers, sortableColumns, sortKeyAt } = useTableColumns(
    "shipmentManifests",
    columns,
  );

  const {
    data: { rows = [], total = 0 } = {},
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["shipment-manifests", page, limit, sortBy, sortDirection, debouncedSearch],
    queryFn: async () => {
      const response = await manifestApi.getAll({
        page,
        limit,
        sortBy,
        order: sortDirection,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      return { rows: response.data.data, total: response.data.total };
    },
    placeholderData: keepPreviousData,
    retry: false,
  });

  const openRow = (row: HawbRow) => void navigate(`/fleet/shipment-manifests/${row.id}`);

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white z-10">
        <Title order={2}>{t("manifests.title")}</Title>
        <div className="w-fit flex flex-wrap items-center justify-end gap-4">
          <TextInput
            placeholder={t("manifests.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-72"
          />
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              variant="outline"
              color="black"
              leftSection={<IconFileImport size={16} />}
              onClick={openImport}
              disabled={!canWrite()}
            >
              {t("manifests.import.button")}
            </Button>
          </PermissionTooltip>
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              leftSection={<IconPlus size={16} />}
              onClick={openCreate}
              disabled={!canWrite()}
            >
              {t("manifests.create.button")}
            </Button>
          </PermissionTooltip>
          <Pagination
            page={page}
            total={total}
            limit={limit}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={[...headers, t("common.actions")]}
          loading={isPending}
          allowSorting
          sortableColumns={sortableColumns}
          onSortChange={(e: TableSortProps) => {
            const key = sortKeyAt(e.index);
            if (!key) return;
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(key);
          }}
          rows={rows.map((row) => (
            <Table.Tr
              key={row.id}
              onClick={() => openRow(row)}
              className={clsx(
                "cursor-pointer",
                row.exit_status === "issue" && "outline outline-2 outline-red-500 -outline-offset-2 bg-red-50/60",
              )}
            >
              {visible.map((column) => (
                <Table.Td key={column.key} className={column.cellClassName}>
                  {column.cell(row)}
                </Table.Td>
              ))}
              <Table.Td className="min-w-[80px]">
                <div className="w-full flex items-center justify-center">
                  <ActionButton
                    icon={IconEye}
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      openRow(row);
                    }}
                  />
                </div>
              </Table.Td>
            </Table.Tr>
          ))}
        />
      </div>

      <ImportManifestModal opened={importOpened} onClose={closeImport} onImported={() => void refetch()} />
      <CreateHawbModal opened={createOpened} onClose={closeCreate} onCreated={() => void refetch()} />
    </div>
  );
};

export default ShipmentManifestsPage;
